import * as gateway from 'sfx-web-contract';
import { EFormatItemValueType } from '../../enums';
import { enumTransformer } from '../object-utils';
import { getCurrenciesFromCcyPair } from '@alk/currency';
import { IFormattedText, INotificationMessage, IPlaceSpotOrderWithData, NotificationItemType } from '../../interfaces';
import { ISO8601 } from '@alk/dates';
import {
    toFixed,
    toShortQty,
} from '@alk/numbers';

export function buildMainNotificationContentForPlaceOrderSend(orderRequest: IPlaceSpotOrderWithData): string {
    const { currencyBase, currencyQuote } = getCurrenciesFromCcyPair(orderRequest.Symbol);
    const isCurrencySwitch = orderRequest.BaseCurrency !== currencyBase;

    return [
        'You requested to',
        orderRequest.Side === gateway.TradeSide.TRADESIDE_BUY ? 'Buy' : 'Sell',
        toShortQty(orderRequest.Quantity),
        isCurrencySwitch ?
            `${ currencyQuote } vs ${ currencyBase }` :
            `${ currencyBase } vs ${ currencyQuote }`,
        `@${ toFixed(5)(orderRequest.Price) },`,
        '07/08/2020',
    ].join(' ');
}

export const notificationItemTypeMap = new Map<gateway.NotificationItemType, NotificationItemType>([
    [ gateway.NotificationItemType.NOTIFICATIONITEMTYPE_BACKTOACTIVE, 'backToActive' ],
    [ gateway.NotificationItemType.NOTIFICATIONITEMTYPE_CANCELLED, 'cancelled' ],
    [ gateway.NotificationItemType.NOTIFICATIONITEMTYPE_EXPIRED, 'expired' ],
    [ gateway.NotificationItemType.NOTIFICATIONITEMTYPE_FILLED, 'filled' ],
    [ gateway.NotificationItemType.NOTIFICATIONITEMTYPE_ORDERCHANGED, 'orderChanged' ],
    [ gateway.NotificationItemType.NOTIFICATIONITEMTYPE_REJECTED, 'rejected' ],
    [ gateway.NotificationItemType.NOTIFICATIONITEMTYPE_SENT, 'sent' ],
    [ gateway.NotificationItemType.NOTIFICATIONITEMTYPE_SUSPENDED, 'suspended' ],
    [ gateway.NotificationItemType.NOTIFICATIONITEMTYPE_TRADINGDISABLED, 'tradingDisabled' ],
    [ gateway.NotificationItemType.NOTIFICATIONITEMTYPE_UNKNOWN, 'unknown' ],
]);

const titleMap = new Map<gateway.NotificationItemType, string>([
    [ gateway.NotificationItemType.NOTIFICATIONITEMTYPE_BACKTOACTIVE, 'Active' ],
    [ gateway.NotificationItemType.NOTIFICATIONITEMTYPE_SENT, 'Order Sent' ],
    [ gateway.NotificationItemType.NOTIFICATIONITEMTYPE_FILLED, 'Order Filled' ],
    [ gateway.NotificationItemType.NOTIFICATIONITEMTYPE_REJECTED, 'Order Rejected' ],
    [ gateway.NotificationItemType.NOTIFICATIONITEMTYPE_SUSPENDED, 'Order Suspended' ],
    [ gateway.NotificationItemType.NOTIFICATIONITEMTYPE_TRADINGDISABLED, 'Trading Disabled' ],
    [ gateway.NotificationItemType.NOTIFICATIONITEMTYPE_CANCELLED, 'Order Canceled' ],
    [ gateway.NotificationItemType.NOTIFICATIONITEMTYPE_EXPIRED, 'Order Expired' ],
    [ gateway.NotificationItemType.NOTIFICATIONITEMTYPE_ORDERCHANGED, 'Amended' ],
]);

const customTopicExtractor = (message: gateway.INotification) => (): string | undefined => {

    if (message.Topic === gateway.NotificationItemType.NOTIFICATIONITEMTYPE_UNKNOWN) {
        return message.CustomHeader;
    }

    return undefined;
};

export function getNotificationTitle(notification: gateway.INotification): string {
    return enumTransformer('notification topic', titleMap, customTopicExtractor(notification))(notification.Topic);
}

const valueTypeMap = new Map<gateway.ValueType, EFormatItemValueType>([
    [ gateway.ValueType.VALUETYPE_DATE, 'date' ],
    [ gateway.ValueType.VALUETYPE_PRICE, 'price' ],
    [ gateway.ValueType.VALUETYPE_QUANTITY, 'quantity' ],
]);

export function transformFormattedText(message: gateway.IFormattedText): IFormattedText {
    return {
        template: message.Template,
        items: message.Items?.map((item) => ({
            key: item.Key,
            value: item.Value,
            type: valueTypeMap.get(item.Type),
        })),
    };
}

export function fullNotificationMap(message: gateway.INotification): INotificationMessage {
    return {
        id: message.Id,
        loaded: true,
        dateCreated: message.Date as ISO8601,
        message: transformFormattedText(message.Message),
        title: getNotificationTitle(message),
        type: notificationItemTypeMap.get(message.Topic) || 'unknown',
        relatedOrderId: message.RelatedOrderId?.value,
        clientId: message.ClientId?.value,
    };
}
